import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ShoppingCartIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  AdjustmentsHorizontalIcon,
  CalendarIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

export default function InventoryChanges() {
  const [searchParams] = useSearchParams();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    inventoryId: searchParams.get('inventoryId') || '',
    changeType: '',
  });

  // Update filters when URL parameters change
  useEffect(() => {
    const inventoryId = searchParams.get('inventoryId');
    if (inventoryId !== filters.inventoryId) {
      setFilters(prev => ({ ...prev, inventoryId }));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchInventory();
    fetchChanges();
  }, [page, filters]);

  const fetchInventory = async () => {
    try {
      const response = await fetch('/api/inventory?limit=100', {
        headers: {
          'X-Employee-ID': localStorage.getItem('employeeId'),
        },
      });
      const data = await response.json();
      setInventory(data.items || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchChanges = async () => {
    try {
      setLoading(true);
      setError(null);
      let url = `/api/inventory/changes?page=${page}&limit=10`;
      if (filters.startDate) url += `&startDate=${filters.startDate}`;
      if (filters.endDate) url += `&endDate=${filters.endDate}`;
      if (filters.inventoryId) url += `&inventoryId=${filters.inventoryId}`;
      if (filters.changeType) url += `&changeType=${filters.changeType}`;

      const response = await fetch(url, {
        headers: {
          'X-Employee-ID': localStorage.getItem('employeeId'),
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch inventory changes');
      }

      setChanges(data.changes || []);
      setTotalPages(Math.ceil((data.pagination?.total || 0) / 10));
    } catch (error) {
      console.error('Error fetching changes:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'purchase':
        return <ShoppingCartIcon className="h-5 w-5 text-green-500" />;
      case 'sale':
        return <ArrowDownIcon className="h-5 w-5 text-red-500" />;
      case 'manual':
        return <ArrowUpIcon className="h-5 w-5 text-blue-500" />;
      case 'adjustment':
        return <AdjustmentsHorizontalIcon className="h-5 w-5 text-yellow-500" />;
      case 'borrow':
        return <ArrowUpIcon className="h-5 w-5 text-purple-500" />;
      case 'gift':
        return <ArrowUpIcon className="h-5 w-5 text-pink-500" />;
      case 'damage':
        return <ArrowDownIcon className="h-5 w-5 text-orange-500" />;
      default:
        return null;
    }
  };

  const getChangeTypeColor = (type) => {
    switch (type) {
      case 'purchase':
        return 'bg-green-100 text-green-800';
      case 'sale':
        return 'bg-red-100 text-red-800';
      case 'manual':
        return 'bg-blue-100 text-blue-800';
      case 'adjustment':
        return 'bg-yellow-100 text-yellow-800';
      case 'borrow':
        return 'bg-purple-100 text-purple-800';
      case 'gift':
        return 'bg-pink-100 text-pink-800';
      case 'damage':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-4">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Inventory Changes</h1>
          <p className="mt-2 text-sm text-gray-700">
            A detailed list of all inventory changes including purchases, sales, and adjustments.
          </p>
        </div>
      </div>

      <div className="mt-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="mt-4 sm:mt-0 sm:flex-none">
            <div className="flex flex-wrap gap-4">
              <div>
                <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">
                  Start Date
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="date"
                    name="start-date"
                    id="start-date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">
                  End Date
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="date"
                    name="end-date"
                    id="end-date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="inventory" className="block text-sm font-medium text-gray-700">
                  Item
                </label>
                <select
                  id="inventory"
                  name="inventory"
                  value={filters.inventoryId}
                  onChange={(e) => setFilters({ ...filters, inventoryId: e.target.value })}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
                >
                  <option value="">All Items</option>
                  {inventory.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="change-type" className="block text-sm font-medium text-gray-700">
                  Change Type
                </label>
                <select
                  id="change-type"
                  name="change-type"
                  value={filters.changeType}
                  onChange={(e) => setFilters({ ...filters, changeType: e.target.value })}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
                >
                  <option value="">All Types</option>
                  <option value="purchase">Purchase</option>
                  <option value="sale">Sale</option>
                  <option value="manual">Manual</option>
                  <option value="adjustment">Adjustment</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Item
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Change Type
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Quantity Change
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        New Quantity
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Date
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Notes
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Image
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {changes.map((change) => (
                      <tr key={change.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          <div className="flex items-center">
                            {getChangeTypeIcon(change.change_type)}
                            <span className="ml-2">{change.inventory.name}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getChangeTypeColor(change.change_type)}`}>
                            {change.change_type.charAt(0).toUpperCase() + change.change_type.slice(1)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span className={change.quantity_change >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {change.quantity_change > 0 ? '+' : ''}{change.quantity_change} {change.inventory.unit}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {change.new_quantity} {change.inventory.unit}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(change.created_at).toLocaleString()}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {change.notes || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {change.image_url ? (
                            <a
                              href={change.image_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group relative inline-block"
                            >
                              <img
                                src={change.image_url}
                                alt="Change documentation"
                                className="h-8 w-8 rounded-md object-cover"
                              />
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 bg-black bg-opacity-50 group-hover:opacity-100 transition-opacity rounded-md">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                                </svg>
                              </div>
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{page}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 